import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isBefore, isFuture } from 'date-fns';
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';

// eslint-disable-next-line no-restricted-imports
import type { CardRepresentation } from '@sorare/trois/src/atoms/Card/Card';
// eslint-disable-next-line no-restricted-imports
import type { Overlay } from '@sorare/trois/src/atoms/Card/Card/types';
import type { CardQuality, SupportedCurrency } from '__generated__/globalTypes';
import {
  BlueprintRevealStatus,
  OfferType,
  Rarity,
  Sport,
} from '__generated__/globalTypes';
import type { ValidWidths } from 'atoms/ui/ResponsiveImg';
import {
  celebrationNormalOverlay,
  celebrationOverlay,
} from 'components/card/Overlay/celebration';
import { earlyAccessOverlay } from 'components/card/Overlay/earlyAccess';
import { statsOverlay } from 'components/card/Overlay/statsOverlay';
import type { GqlType } from 'lib/gql';
import { withFragments } from 'lib/gql';

import type {
  getAnimatedCardVideoSources_anyCardInterface,
  getAnimatedCardVideoSources_so5Appearance,
  getCardRepresentation_anyCardInterface,
  getHumanReadableSerialNumber_anyCard,
  getName_anyCard,
  getOverlays_anyCardInterface,
  isMyCardListedOnMarket_anyCard,
  isSentInDirectOffer_anyCard,
  isTokenListedOnMarket_anyCard,
} from './__generated__/cards.graphql';
import { sortBy } from './arrays';
import sealedURL from './assets/sealed.gltf?url';
import { formatWord } from './humanize';
import type { MonetaryAmountParams } from './monetaryAmount';

export const CARD_SIZE = 320;
export const CARD_ASPECT_RATIO = 50 / 81;
export const CARD_ASPECT_RATIO_SMALL = 16 / 17;

export const getCardBorderRadiusWithOffsets = (
  cardBorderRadiusInPx = 24,
  offsetWidth = 0,
  offsetHeight = 0,
  aspectRatio = CARD_ASPECT_RATIO,
  cardWidth = 250
) => {
  return `${(100 / (cardWidth + offsetWidth)) * cardBorderRadiusInPx}% /
  ${(100 / (cardWidth / aspectRatio + offsetHeight)) * cardBorderRadiusInPx}%`;
};

/* border radius in % to be able to scale - only valid for 2022+ cards */
export const CARD_BORDER_RADIUS = getCardBorderRadiusWithOffsets();
export const CARD_BORDER_RADIUS_2024 = getCardBorderRadiusWithOffsets(
  40,
  0,
  0,
  CARD_ASPECT_RATIO,
  772
);

export interface Card extends GqlType {
  slug: string;
  liveSingleSaleOffer?: {
    receiverSide: {
      amounts: MonetaryAmountParams;
    };
  } | null;
  latestEnglishAuction?: {
    id: string;
    currentPrice: string;
    currency: SupportedCurrency;
    endDate: Date;
    open: boolean;
  } | null;
  livePrimaryOffer?: {
    price: MonetaryAmountParams | null;
    endDate: Date;
    nfts?:
      | {
          __typename: 'Token';
        }[]
      | null;
  } | null;
  user?: { __typename: 'User' } | null;
  tokenOwner?: {
    amounts: MonetaryAmountParams | null;
    user?: { __typename: 'User' } | null;
    from?: Date | null;
  } | null;
  priceRange?: { min: bigint; max: bigint } | null;
}

export const blockchainRarities = [
  'custom_series',
  'limited',
  'rare',
  'super_rare',
  'unique',
] as const;

export const playableBlockchainRarities = [
  'limited',
  'rare',
  'super_rare',
  'unique',
] as const;

export const rarities = ['common', ...blockchainRarities] as const;

export const subscribableRarities = ['rare', 'super_rare', 'unique'];
export const usSportPlayableRarities = playableBlockchainRarities;
export const usSportSubscribableRarities = ['super_rare', 'unique'];

export type BlockchainScarcity = (typeof blockchainRarities)[number];
export type Scarcity = (typeof rarities)[number];

type BlockchainCamelCaseScarcity =
  | 'customSeries'
  | 'limited'
  | 'rare'
  | 'superRare'
  | 'unique';
export type CamelCaseScarcity = 'common' | BlockchainCamelCaseScarcity;

export const scarcityNames: { [name: string]: string } = {
  common: 'Common',
  custom_series: 'Custom Series',
  limited: 'Limited',
  rare: 'Rare',
  super_rare: 'Super Rare',
  superRare: 'Super Rare',
  unique: 'Unique',
  mix: 'Mixed',
};

export const OLD_US_SPORTS_SUPPLY = {
  [Rarity.unique]: 1,
  [Rarity.super_rare]: 100,
  [Rarity.rare]: 1000,
  [Rarity.limited]: 5000,
} as Record<Rarity, number>;

export const SUPPLY = {
  [Rarity.unique]: 1,
  [Rarity.super_rare]: 10,
  [Rarity.rare]: 100,
  [Rarity.limited]: 1000,
  [Rarity.custom_series]: 2022,
} as Record<Rarity, number>;

export const formatScarcity = (
  rarity: Scarcity | string,
  cardEdition?: string | null,
  shorten?: boolean
) => {
  if (rarity === 'custom_series' && cardEdition) {
    return cardEdition;
  }
  const fullName = scarcityNames[rarity] || formatWord(rarity);
  if (!shorten) return fullName;
  const shorthands: Record<string, string> = {
    Limited: 'Limited',
    Rare: 'Rare',
    'Super Rare': 'S-Rare',
    Unique: 'Unique',
    Common: 'Common',
  };

  return shorthands[fullName] || fullName;
};

const nonEnumerableScarcities = ['unique', 'common'];

export const getHumanReadableScarcity = (
  card: {
    rarity: string;
    serialNumber: number;
  },
  { forceSerialNumber = false, oldUSSupply = false } = {}
) => {
  if (card.rarity === 'common') return formatScarcity(card.rarity);
  if (nonEnumerableScarcities.includes(card.rarity) && !forceSerialNumber)
    return formatScarcity(card.rarity);

  return `${card.serialNumber}/${
    (oldUSSupply ? OLD_US_SPORTS_SUPPLY : SUPPLY)[card.rarity as Rarity]
  }`;
};

export const getHumanReadableQuality = (quality: CardQuality) => {
  return quality.replace(/TIER_(.)/, 'T$1');
};

export const getHumanReadableSerialNumber = withFragments(
  (item_: getHumanReadableSerialNumber_anyCard, { separator = ' ' } = {}) => {
    const item = item_;
    const formattedRarity = item.displayRarity;

    if (nonEnumerableScarcities.includes(item.rarityTyped)) {
      return formattedRarity;
    }

    const serial = `${item.serialNumber}/${item.supply}`;
    return `${formattedRarity}${separator}${serial}`;
  },
  {
    anyCard: gql`
      fragment getHumanReadableSerialNumber_anyCard on AnyCardInterface {
        slug
        displayRarity
        rarityTyped
        serialNumber
        supply
      }
    ` as TypedDocumentNode<getHumanReadableSerialNumber_anyCard>,
  }
);

export const getName = withFragments(
  (
    item: getName_anyCard,
    { separator = ' ', nameSeparator = ' \u2013 ' } = {}
  ) => {
    return [
      item.anyPlayer.displayName,
      nameSeparator,
      item.sportSeason.name,
      nameSeparator,
      getHumanReadableSerialNumber(item, { separator }),
    ].join('');
  },
  {
    anyCard: gql`
      fragment getName_anyCard on AnyCardInterface {
        slug
        sportSeason {
          name
        }
        anyPlayer {
          slug
          displayName
        }
        ...getHumanReadableSerialNumber_anyCard
      }
      ${getHumanReadableSerialNumber.fragments.anyCard}
    ` as TypedDocumentNode<getName_anyCard>,
  }
);

export const sortHitByRarity = (items: RefinementListItem[]) =>
  sortBy(
    item => {
      return (rarities as unknown as string[]).indexOf(item.label);
    },
    [...items]
  );

interface XpCard {
  xp: number;
  xpNeededForNextGrade: number | null;
  xpNeededForCurrentGrade: number;
}

export const percentageToNextLevel = ({
  xp,
  xpNeededForCurrentGrade,
  xpNeededForNextGrade,
}: XpCard) => {
  if (xpNeededForNextGrade === xpNeededForCurrentGrade) {
    return 0;
  }

  return xpNeededForNextGrade
    ? ((xp - xpNeededForCurrentGrade) /
        (xpNeededForNextGrade - xpNeededForCurrentGrade)) *
        100
    : 100;
};

export const CARD_LEVEL_MIN = 0;

export const APPEARANCES_MIN = 0;
export const APPEARANCES_5_MAX = 5;
export const APPEARANCES_15_MAX = 15;

const isTokenListedOnMarket = withFragments(
  (token_: isTokenListedOnMarket_anyCard | null) => {
    const token = token_;
    // sentInLiveOffers will only have data if the card belongs to the currentUser
    return (token?.sentInLiveOffers ?? []).some(
      offer =>
        offer.type === OfferType.SINGLE_SALE_OFFER &&
        // handle case when token listing ended but frontend hasn't
        // refetched the data yet
        isFuture(offer.endDate)
    );
  },
  {
    anyCard: gql`
      fragment isTokenListedOnMarket_anyCard on AnyCardInterface {
        slug
        sentInLiveOffers {
          id
          type
          endDate
        }
      }
    ` as TypedDocumentNode<isTokenListedOnMarket_anyCard>,
  }
);

export const isMyCardListedOnMarket = withFragments(
  (card_: isMyCardListedOnMarket_anyCard) => {
    const card = card_;
    return isTokenListedOnMarket(card);
  },
  {
    anyCard: gql`
      fragment isMyCardListedOnMarket_anyCard on AnyCardInterface {
        slug
        ...isTokenListedOnMarket_anyCard
      }
      ${isTokenListedOnMarket.fragments.anyCard}
    ` as TypedDocumentNode<isMyCardListedOnMarket_anyCard>,
  }
);

export const isSentInDirectOffer = withFragments(
  (card_: Nullable<isSentInDirectOffer_anyCard>) => {
    const card = card_;
    return (card?.sentInLiveOffers ?? []).some(
      offer =>
        offer.type === OfferType.DIRECT_OFFER &&
        // handle case when token listing ended but frontend hasn't
        // refetched the data yet
        isFuture(offer.endDate)
    );
  },
  {
    anyCard: gql`
      fragment isSentInDirectOffer_anyCard on AnyCardInterface {
        slug
        sentInLiveOffers {
          id
          type
          endDate
        }
      }
    ` as TypedDocumentNode<isSentInDirectOffer_anyCard>,
  }
);

export const getAnimatedCardVideoSources = withFragments(
  (
    card_:
      | getAnimatedCardVideoSources_anyCardInterface
      | getAnimatedCardVideoSources_so5Appearance,
    width: ValidWidths = 640
  ): { src: string; type: string }[] => {
    const card = card_;
    const { webmVideoUrl, webmLowResVideoUrl } = card;

    const optimalWebmUrl =
      width > 320 ? webmVideoUrl : webmLowResVideoUrl || webmVideoUrl; // tmp fallback until low res webm are generated

    return [
      optimalWebmUrl
        ? { src: optimalWebmUrl, type: 'video/webm; codecs="vp9"' }
        : null,
    ].filter(Boolean);
  },
  {
    anyCardInterface: gql`
      fragment getAnimatedCardVideoSources_anyCardInterface on AnyCardInterface {
        slug
        webmVideoUrl: videoUrl
        webmLowResVideoUrl: videoUrl(derivative: "low_res")
      }
    ` as TypedDocumentNode<getAnimatedCardVideoSources_anyCardInterface>,
    so5Appearance: gql`
      fragment getAnimatedCardVideoSources_so5Appearance on So5AppearanceInterface {
        id
        webmVideoUrl: videoUrl
        webmLowResVideoUrl: videoUrl(derivative: "low_res")
      }
    ` as TypedDocumentNode<getAnimatedCardVideoSources_so5Appearance>,
  }
);

export const getOverlays = withFragments(
  (card: getOverlays_anyCardInterface): Overlay[] | undefined => {
    const editionName = card.cardEditionName?.toLowerCase();
    if (editionName === 'celebration_sealed') {
      return [
        { mapName: 'card_back', method: celebrationOverlay(card) },
        {
          mapName: 'card_back_normal',
          mapType: 'normalMap',
          method: celebrationNormalOverlay(card),
        },
      ];
    }
    if (
      card.blueprint &&
      card.blueprint.revealStatus !== BlueprintRevealStatus.REVEALED
    ) {
      return [{ mapName: 'card_back', method: earlyAccessOverlay(card) }];
    }

    const overlay = statsOverlay(card);

    return overlay ? [{ mapName: 'card_back', method: overlay }] : undefined;
  },
  {
    anyCardInterface: gql`
      fragment getOverlays_anyCardInterface on AnyCardInterface {
        slug
        blueprint {
          id
        }
        cardEditionName
        ...statsOverlay_AnyCardInterface
        ...earlyAccessOverlay_AnyCardInterface
        ...celebrationOverlay_AnyCardInterface
        ...celebrationNormalOverlay_AnyCardInterface
      }
      ${statsOverlay.fragments.AnyCardInterface}
      ${earlyAccessOverlay.fragments.AnyCardInterface}
      ${celebrationOverlay.fragments.AnyCardInterface}
      ${celebrationNormalOverlay.fragments.AnyCardInterface}
    ` as TypedDocumentNode<getOverlays_anyCardInterface>,
  }
);

export const getCardRepresentation = withFragments(
  (
    card_: getCardRepresentation_anyCardInterface,
    options: { rewrapCard?: boolean } = {
      rewrapCard: false,
    }
  ): CardRepresentation => {
    const card = card_;
    const { rewrapCard } = options;

    const showBlueprint =
      card.blueprint &&
      (card.blueprint.revealStatus !== BlueprintRevealStatus.REVEALED ||
        rewrapCard);

    const sealedUrl: string | undefined = card.sealed ? sealedURL : undefined;

    const gltfUrl = showBlueprint
      ? card.blueprint!.gltfUrl
      : card.threeDimensionalAsset?.gltfUrl;

    // 2d
    if (!gltfUrl) {
      return { frontUrl: card.pictureUrl!, backUrl: card.backPictureUrl };
    }

    const overlays = getOverlays(card);

    const isSeason24 = card.blueprint?.launchDate
      ? isBefore(card.blueprint?.launchDate, new Date('2025-06-01'))
      : false;
    const year: number | undefined = isSeason24 ? 2024 : undefined;

    let state: BlueprintRevealStatus | undefined;

    if (showBlueprint) {
      if (rewrapCard) {
        state = BlueprintRevealStatus.REVEALABLE;
      } else {
        state = card.blueprint?.revealStatus;
      }
    }

    return {
      gltfUrl,
      sealedUrl,
      state,
      year,
      videoSources: getAnimatedCardVideoSources(card),
      overlays,
    };
  },
  {
    anyCardInterface: gql`
      fragment getCardRepresentation_anyCardInterface on AnyCardInterface {
        slug
        pictureUrl
        cardEditionName
        backPictureUrl
        sealed
        blueprint {
          id
          launchDate
          revealDate
          revealStatus
          gltfUrl
        }
        threeDimensionalAsset {
          gltfUrl
        }
        ...getAnimatedCardVideoSources_anyCardInterface
        ...statsOverlay_AnyCardInterface
        ...earlyAccessOverlay_AnyCardInterface
        ...celebrationOverlay_AnyCardInterface
        ...getOverlays_anyCardInterface
      }
      ${getAnimatedCardVideoSources.fragments.anyCardInterface}
      ${getOverlays.fragments.anyCardInterface}
    ` as TypedDocumentNode<getCardRepresentation_anyCardInterface>,
  }
);

export const isSamePlayerSameSeason = (
  cardSlug1: string,
  cardSlug2: string
) => {
  // Split both strings at the last hyphen
  const base1 = cardSlug1.split('-').slice(0, -1).join('-');
  const base2 = cardSlug2.split('-').slice(0, -1).join('-');

  return base1 === base2;
};

export const isVaultableRarity = (
  rarity: Rarity
): rarity is Exclude<Rarity, 'common' | 'custom_series'> => {
  return rarity !== 'common' && rarity !== 'custom_series';
};

export const earlyAccessBlogUrl = {
  [Sport.FOOTBALL]: '/help/s/20047423006877/early-access-cards',
  [Sport.NBA]: '/help/s/30756990994077/Early%20Access%20Cards',
  // FIXME it's the football page
  [Sport.BASEBALL]: '/help/s/20047423006877/early-access-cards',
};
